import { downloadCSV } from "ra-core";
import jsonExport from "jsonexport/dist";
import {
  calculateLf,
  calculateMbf,
  calculateMsf,
  getUnitName,
  isEngineeredLumberCommodity,
  isLumberCommodity,
  isPanelsCommodity,
} from "../../../utils/loadTypeUtils";
import { getFormattedStartAndEndDate } from "../../../utils/dateUtils";
import { exportCustomField, trimDecimal } from "../../../utils/utils";
import { buildHeader, fillDownRows } from "../../../utils/exportsUtils";
import { FilePaths, Orientations, Unit } from "../../../utils/constants";
import { parseCustomFieldToObject } from "../../../components/common/CustomFields";
import { logError } from "../../../utils/appUtils";
import {
  generateAndDownloadPdf,
  generateFileName,
} from "../../../utils/fileUtils";

export const inventoryExportPdf = async (
  data,
  _inputStartDate,
  _inputEndDate,
  notify,
  filter,
) => {
  try {
    const reloadYardId = window.appUser?.reloadYardId;
    debugger;

    const { formattedStartDate, formattedEndDate } =
      getFormattedStartAndEndDate(data, new Date(), new Date(), "receivedAt");

    // Initialize totals and flags
    let totalPieces = 0;
    let totalMBF = 0;
    let totalMSF = 0;
    let totalLF = 0;
    let showPPU = false;
    let showFootage = false;
    let totalsMap = {};

    // Process inventories
    const items = data.map((item) => {
      // Destructure item properties with default values
      const {
        reloadYardCommodityId,
        pcs,
        productName = "--",
        productCode = "--",
        product,
        quantityOnHand = 0,
        accountName = "--",
        customFields = [],
        inventoryUOM,
      } = item;

      // Parse custom fields
      const cleanCustomFields = customFields?.map(parseCustomFieldToObject);

      // Calculate pieces and units
      const pieces = pcs || product?.pcs;
      const units = parseInt(quantityOnHand) || 0;
      totalPieces += pieces ? pieces * units : 0;

      // Initialize footage variables
      let footage = null;
      let footageUOM = "";

      // Calculate footage based on commodity type
      if (isLumberCommodity(reloadYardCommodityId)) {
        footage = calculateMbf(item);
        footageUOM = Unit.MBF;
        totalMBF += footage * units;
      } else if (isPanelsCommodity(reloadYardCommodityId)) {
        footage = calculateMsf(item);
        footageUOM = Unit.MSF;
        totalMSF += footage * units;
      } else if (isEngineeredLumberCommodity(reloadYardCommodityId)) {
        footage = calculateLf(item);
        footageUOM = Unit.LF;
        totalLF += footage * units;
      }

      // Set display flags
      showPPU = showPPU || !!pieces;
      showFootage = showFootage || !!footage;

      // Update totals map
      if (totalsMap[inventoryUOM]) {
        totalsMap[inventoryUOM] += units;
      } else {
        totalsMap[inventoryUOM] = units;
      }

      // Return processed inventory item
      return {
        accountName: accountName,
        productName: productName,
        productCode: productCode,
        customFields: cleanCustomFields,
        ppu: !!pieces ? trimDecimal(pieces)?.toLocaleString() : "--",
        units: `${parseInt(units)?.toLocaleString()} ${getUnitName(
          inventoryUOM,
        )}`,
        footage:
          footage !== null
            ? `${trimDecimal(footage * units)?.toLocaleString()} ${footageUOM}`
            : "--",
      };
    });

    const fileName = generateFileName({ resource: "inventory", ext: "pdf" });
    const downloadName = generateFileName({
      resource: "inventory",
      ext: "pdf",
      includeDate: true,
    });

    // Build columns array
    const columns = [
      { label: "Product Name", source: "productName" },
      { label: "Product Code", source: "productCode" },
    ];

    if (!filter?.accountId?.eq) {
      columns.unshift({ label: "Customer", source: "accountName" });
    }

    if (showPPU) {
      columns.push({ label: "PPU", source: "ppu" });
    }

    columns.push({ label: "Quantity", source: "units" });

    if (showFootage) {
      columns.push({ label: "Footage", source: "footage" });
    }

    // Build header
    const header = buildHeader(
      data,
      formattedStartDate,
      formattedEndDate,
      filter,
    );

    // Build footer
    const footer = [
      {
        label: "Totals",
        value: "",
      },
    ];

    // Add totals breakdown to the footer
    if (Object.keys(totalsMap).length > 0) {
      for (const [uom, value] of Object.entries(totalsMap)) {
        footer.push({
          label: `${uom}`,
          value: trimDecimal(value).toLocaleString(),
        });
      }
    }

    if (!!totalPieces) {
      footer.push({
        label: "Pieces",
        value: trimDecimal(totalPieces).toLocaleString(),
      });
    }

    if (totalMBF) {
      footer.push({
        label: "MBF",
        value: trimDecimal(totalMBF).toLocaleString(),
      });
    }

    if (totalMSF) {
      footer.push({
        label: "MSF",
        value: trimDecimal(totalMSF, 3).toLocaleString(),
      });
    }

    if (totalLF) {
      footer.push({
        label: "LF",
        value: trimDecimal(totalLF).toLocaleString(),
      });
    }

    // Generate the PDF report
    const templateKey = `${FilePaths.TEMPLATES_PDF}/${FilePaths.TEMPLATE_REPORT}`;
    const outputFileKey = `${reloadYardId}/${reloadYardId}/${FilePaths.INVENTORY}/${fileName}`;
    await generateAndDownloadPdf({
      templateKey,
      outputFileKey,
      downloadName,
      data: {
        metaData: {
          orientation: Orientations.PORTRAIT,
          currentDate: new Date().toLocaleString(),
        },
        content: {
          header,
          title: "INVENTORY REPORT",
          table: {
            columns,
            items,
          },
          footer,
        },
      },
    });
  } catch (err) {
    logError("inventory:exportPdf", err, notify);
  }
};

export const inventoryExportCsv = (inventories) => {
  const customHeaderMap = new Map();
  debugger;

  const inventoriesForExport = inventories.map((inventory) => {
    const inventoryForExport = {
      inventoryId: inventory.id,
      accountName: inventory.accountName,
      productCode: inventory.productCode,
      productName: inventory.productName,
      lotNumber: inventory.lotNumber,
      poNumber: inventory.poNumber,
      carTralNumber: inventory.railCarNumber,
      dateReceived: inventory.receivedAt
        ? new Date(inventory.receivedAt).toLocaleDateString()
        : "",
      totalOnHand: inventory.quantityOnHand,
      totalCommitted: inventory.quantityCommitted || 0,
      units: inventory.inventoryUOM,
      pieces: inventory.pcs || inventory.product?.pcs || "",
    };

    if (inventory.customFields?.length > 0) {
      inventory.customFields.forEach((field) => {
        exportCustomField(field, inventoryForExport, customHeaderMap);
      });
    }

    if (inventory.inventoryItems?.items) {
      inventoryForExport.inventoryItem = inventory.inventoryItems.items.map(
        (inventoryItem) => {
          const lineItem = {
            itemId: inventoryItem.id,
            location: inventoryItem.locationFullName,
            quantityOnHand: inventoryItem.quantityOnHand,
            quantityCommitted: inventoryItem.quantityCommitted || 0,
            damaged: inventoryItem.isDamaged
              ? inventoryItem.originOfDamage
              : "",
          };

          return lineItem;
        },
      );
    }

    return inventoryForExport;
  });

  jsonExport(fillDownRows(inventoriesForExport), { rename: ["inventoryId"] }, (err, csv) => {
    downloadCSV(csv, `inventories_${new Date().toLocaleDateString()}`); // download as 'inventories_DATE.csv` file
  });
};
